import { useEffect, useMemo, useState } from 'react';
import { ReportSubscriptionsPanel } from './subscriptions-panel.js';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Button,
  ChartWidget,
  DataTable,
  ErrorState,
  Num,
  Panel,
  RuleBuilder,
  SectionLabel,
  Skeleton,
  type ChartSegment,
  type ChartShape,
} from '@whinerp/ui';
import {
  api,
  type ApiError,
  type ListReportSourcesResult,
  type ListViewsResult,
  type PreviewReportResult,
  type ReportSpec,
} from '@whinerp/client';
import type { RuleNode, RuleSchema } from '@whinerp/contracts/rules';
import type { FieldType } from '@whinerp/contracts/fields';
import { formatCount, formatMoney, formatPercent } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { ruleBuilderLabels } from '../catalog/rule-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-23 Report builder.
 *
 * Three panes: fields on the left, the preview in the middle, settings on the
 * right. The preview updates on every change and there is no run button -
 * report builders that require a run click get abandoned, and a preview that
 * lags a change is a preview of the wrong report.
 *
 * Two things the builder refuses to do, both of them about not losing work:
 *
 * **An invalid spec does not blank the canvas.** The problems appear inline
 * and the last version that ran stays on screen underneath them. Somebody
 * mid-edit has not asked to lose what they had.
 *
 * **The filter builder is the RuleBuilder, reading a schema built from the
 * source's own fields.** A report's filters are the same document as a
 * pricelist's and a segment's; what differs is the fields, which is the
 * parameter. A sixth rule builder would be a sixth place for `contains` to
 * mean something slightly different.
 */

type Sources = ListReportSourcesResult;
type Source = Sources['sources'][number];
type Preview = PreviewReportResult;

const CHART_SHAPES: ChartShape[] = ['column', 'bar', 'table', 'line', 'donut', 'funnel'];
const FUNCTIONS = ['count', 'sum', 'avg', 'min', 'max'] as const;

/**
 * A report field's type, in the rule engine's vocabulary.
 *
 * The two vocabularies overlap but are not the same: a report calls a money
 * column 'money' because that is what it formats it as, and the rule engine
 * calls it 'currency' because that is what it offers operators for. Written as
 * a map rather than a ternary chain so a report type added later fails to
 * compile here instead of silently arriving as text - which would offer
 * `contains` on an amount.
 */
const RULE_TYPE: Record<Source['fields'][number]['type'], FieldType> = {
  text: 'text',
  number: 'number',
  money: 'currency',
  date: 'date',
  boolean: 'boolean',
  user: 'user',
  option: 'picklist',
};

/** The source's fields, as a rule schema. One builder, one document, a schema per caller. */
function schemaFor(source: Source): RuleSchema {
  return {
    key: `report:${source.objectKey}`,
    label: source.label,
    outcome: source.pluralLabel,
    maxDepth: 3,
    // Mirrors the schema the server builds for the same source: a report with
    // no filters is all of them.
    emptyMeans: 'everything',
    fields: source.fields.map((field) => ({
      key: field.key,
      label: field.label,
      type: RULE_TYPE[field.type],
    })),
  };
}

function ReportBuilderScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const params = useParams();
  const reportId = params['id'] && params['id'] !== 'new' ? params['id'] : null;

  const sources = useQuery<Sources, ApiError>({
    queryKey: ['report-sources'],
    queryFn: () => api.listReportSources({}),
  });

  const saved = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => api.getReport({ path: { reportId: reportId ?? '' } }),
    enabled: reportId !== null,
  });

  const [spec, setSpec] = useState<ReportSpec | null>(null);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'workspace'>('private');

  // Opens on something rather than nothing: the first source's blank spec,
  // which already groups by its first groupable field.
  useEffect(() => {
    if (spec !== null) return;
    if (saved.data) {
      setSpec(saved.data.report.spec);
      setName(saved.data.report.name);
      setVisibility(saved.data.report.visibility);
      return;
    }
    if (!reportId && sources.data?.sources[0]) setSpec(sources.data.sources[0].blank);
  }, [sources.data, saved.data, spec, reportId]);

  /*
   * The saved views for the source on screen (F-021).
   *
   * Keyed by the object, so switching source fetches that object's views
   * rather than showing the last one's - a picker offering a contact view
   * while the report reads deals is a refusal waiting to happen.
   */
  const views = useQuery<ListViewsResult, ApiError>({
    queryKey: ['views', spec?.objectKey ?? null],
    queryFn: () => api.listViews({ path: { objectKey: spec?.objectKey ?? '' } }),
    enabled: spec !== null,
  });

  const preview = useQuery<Preview, ApiError>({
    queryKey: ['report-preview', JSON.stringify(spec)],
    queryFn: () => api.previewReport({ body: { spec: spec as ReportSpec } }),
    enabled: spec !== null,
    /*
     * The last preview stays while the next one is fetched.
     *
     * Without it the whole middle pane - including the controls that changed
     * the spec - would go to a skeleton on every keystroke (P-27).
     */
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: () =>
      api.saveReport({
        body: {
          ...(reportId ? { reportId } : {}),
          name: name.trim() || t('report.title'),
          spec: spec as ReportSpec,
          visibility,
        },
      }),
  });

  const source = useMemo(
    () => sources.data?.sources.find((s) => s.objectKey === spec?.objectKey) ?? null,
    [sources.data, spec?.objectKey],
  );

  if (sources.isError) {
    return <ReportBuilderError error={sources.error} retry={() => void sources.refetch()} />;
  }
  if (!spec || !source) return <ReportBuilderLoading />;

  const update = (patch: Partial<ReportSpec>): void => setSpec({ ...spec, ...patch });

  return (
    <div data-component="ReportBuilder" className="flex w-full min-w-0 gap-4">
      {/* Left: the fields this source offers. */}
      <aside
        aria-label={t('report.fields')}
        className="flex w-panel-narrow shrink-0 flex-col gap-2"
        data-pane="fields"
      >
        <SectionLabel>{t('report.source')}</SectionLabel>
        <label className="flex flex-col gap-1 text-dense text-ink">
          <span className="sr-only">{t('report.source')}</span>
          <select
            data-source-select
            aria-label={t('report.source')}
            value={spec.objectKey}
            onChange={(event) => {
              // A different object has different fields, so the old grouping
              // and filters cannot survive. Starting from that source's blank
              // spec is honest; carrying them over would produce a report that
              // silently filtered on nothing.
              const next = sources.data?.sources.find((s) => s.objectKey === event.target.value);
              if (next) setSpec(next.blank);
            }}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          >
            {(sources.data?.sources ?? []).map((option) => (
              <option key={option.objectKey} value={option.objectKey}>
                {option.pluralLabel}
              </option>
            ))}
          </select>
        </label>

        {/*
          * Start from a saved view (F-021).
          *
          * The view's filter narrows this report ALONGSIDE the filter below,
          * by AND. Replacing the report's own filter would make a person's
          * conditions vanish the moment they picked a view.
          *
          * Only shown when this object has views to pick: an empty dropdown
          * beside a source that has none is a control that answers "no" to
          * every use of it.
          */}
        {views.data && views.data.views.length > 0 ? (
          <label className="flex flex-col gap-1 text-dense text-ink">
            <span className="text-micro uppercase tracking-label text-ink-label">
              {t('report.view')}
            </span>
            <select
              data-report-view
              aria-label={t('report.view')}
              value={spec.viewId ?? ''}
              onChange={(event) => {
                const chosen = event.target.value;
                // Null rather than absent, so clearing the choice is a change
                // the saved spec records rather than a field that stays put.
                update({ viewId: chosen === '' ? null : chosen });
              }}
              className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
            >
              <option value="">{t('report.view.none')}</option>
              {views.data.views.map((option) => (
                <option key={option.viewId} value={option.viewId}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <SectionLabel>{t('report.fields')}</SectionLabel>
        <ul className="flex flex-col gap-0.5" data-field-list>
          {source.fields.map((field) => (
            <li
              key={field.key}
              data-field={field.key}
              className="flex items-baseline justify-between gap-2 rounded-control px-2 py-1 text-dense text-ink"
            >
              <span className="truncate">{field.label}</span>
              {/* What each field can do, said here rather than discovered by a
                  control that refuses. */}
              <span className="flex shrink-0 gap-1 text-meta text-ink-meta">
                {field.groupable ? <span data-can-group>{t('report.group')}</span> : null}
                {field.summarisable ? <span data-can-summarise>{t('report.value')}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </aside>

      {/* Centre: the preview. No run step. */}
      <section className="flex min-w-0 flex-1 flex-col gap-2" data-pane="preview">
        <header className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('report.preview')}</SectionLabel>
          <span className="text-meta text-ink-meta">{t('report.livePreview')}</span>
        </header>

        {preview.data && preview.data.problems.length > 0 ? (
          <div
            role="alert"
            data-report-problems
            className="flex flex-col gap-1 rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text"
          >
            <span>{t('report.problems')}</span>
            <ul className="flex flex-col gap-0.5">
              {preview.data.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
            {/* The previous preview is still below. Said out loud so nobody
                reads a stale chart as the current one. */}
            <span className="text-meta">{t('report.lastValid')}</span>
          </div>
        ) : null}

        <Panel>
          {/*
            * How many records the report covers.
            *
            * From the engine's count(), beside the chart rather than inside
            * it: a chart says how the records divide and this says how many
            * there are, and somebody who has just narrowed a report by a view
            * or a filter is asking the second question.
            */}
          {preview.data?.result ? (
            <p className="flex items-baseline gap-2 pb-3">
              <span className="text-micro uppercase tracking-label text-ink-label">
                {t('report.records')}
              </span>
              <Num data-report-total className="font-serif text-h3 text-ink">
                {formatCount(preview.data.result.totalCount, format)}
              </Num>
            </p>
          ) : null}

          {/*
            * All currencies, in the reporting one (F-023).
            *
            * Beside the single-currency figures rather than instead of them,
            * and the gap is drawn with it: a converted total is exactly the
            * figure that can look complete while leaving amounts out.
            */}
          {preview.data?.result?.convertedTotal ? (
            <p data-converted-total className="flex flex-wrap items-baseline gap-2 pb-3">
              <span className="text-micro uppercase tracking-label text-ink-label">
                {t('report.converted', {
                  values: { code: preview.data.result.convertedTotal.currency },
                })}
              </span>
              <Num className="font-serif text-h3 text-ink">
                {formatMoney(preview.data.result.convertedTotal.total, format, {
                  currency: preview.data.result.convertedTotal.currency,
                })}
              </Num>
              {preview.data.result.convertedTotal.unconvertible.map((gap) => (
                <Num
                  key={`${gap.from}-${gap.on}`}
                  data-converted-gap={gap.from}
                  className="text-dense text-warning-text"
                >
                  {t('report.converted.gap', {
                    values: { count: formatCount(gap.count, format), from: gap.from },
                  })}
                </Num>
              ))}
            </p>
          ) : null}

          {preview.isPending ? (
            <Skeleton className="h-plot w-full" />
          ) : (
            <ChartWidget
              data-report-chart
              shape={spec.chart}
              segments={segmentsOf(preview.data, format)}
              scope={
                preview.data?.result?.escalation
                  ? { withheld: formatCount(preview.data.result.escalation.withheld, format) }
                  : undefined
              }
              labels={{
                chart: t('report.preview'),
                drillThrough: (segment) => t('report.drillThrough', { values: { segment } }),
                empty: t('report.empty'),
                group: t('report.group'),
                value: t('report.value'),
                widerThanRows: (withheld) => t('report.widerThanRows', { values: { withheld } }),
                noDrillThrough: t('report.noDrillThrough'),
                // Signed and named, like the dashboard's. A bare percentage on
                // a funnel step says nothing about what it is a share of.
                stepChange: (ratio, previousStep) =>
                  t('chart.ofPrevious', {
                    values: {
                      change: formatPercent(ratio, format, { sign: true }),
                      step: previousStep,
                    },
                  }),
              }}
            />
          )}
        </Panel>

        {/* Sent on a schedule, figures in the email (F-099). */}
        {reportId ? <ReportSubscriptionsPanel reportId={reportId} /> : null}

        {/* The evidence. A report is a summary AND the records behind it, on
            the same screen, so nobody has to take the chart on trust. */}
        {preview.data?.result && preview.data.result.rows.length > 0 ? (
          <Panel>
            <SectionLabel>{t('report.rows')}</SectionLabel>
            <DataTable
              /*
               * The columns here ARE the report: somebody chose them. Applying a
               * display preference on top would silently return a different
               * report than the one that was built.
               */
              mode="advanced"
              data-report-rows
              label={t('report.rows')}
              rows={preview.data.result.rows}
              rowId={(row) => String(row['__id'] ?? '')}
              columns={preview.data.result.rowColumns.map((column) => ({
                key: column.key,
                header: column.label,
                cell: (row: Record<string, string | null>) => row[column.key] ?? '',
              }))}
              labels={{
                sortBy: (column) => t('list.sortBy', { values: { column } }),
                selectRow: (row) => t('list.selectRow', { values: { row } }),
                rowsShown: (count) => t('list.rowsShown', { values: { count: formatCount(count, format) } }),
              }}
            />
            {preview.data.result.rowsTruncated ? (
              <p data-rows-truncated className="pt-1 text-meta text-ink-meta">
                {t('report.rowsTruncated')}
              </p>
            ) : null}
          </Panel>
        ) : null}
      </section>

      {/* Right: what the report is. */}
      {/* Both panes are landmarks, so both are named: two unlabelled
          asides are one landmark a screen-reader user cannot tell from the
          other, which is what axe reports here. */}
      <aside
        aria-label={t('report.builder')}
        className="flex w-panel-wide shrink-0 flex-col gap-3"
        data-pane="settings"
      >
        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('report.name')}
          <input
            data-report-name
            aria-label={t('report.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          />
        </label>

        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('report.groupBy')}
          <select
            data-group-by
            aria-label={t('report.groupBy')}
            value={spec.groupBy[0] ?? ''}
            onChange={(event) => update({ groupBy: event.target.value ? [event.target.value] : [] })}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          >
            <option value="">{t('report.records')}</option>
            {/* Only fields that can group. A control offering a choice the
                server will refuse is a control that teaches distrust. */}
            {source.fields
              .filter((field) => field.groupable)
              .map((field) => (
                <option key={field.key} value={field.key}>
                  {field.label}
                </option>
              ))}
          </select>
        </label>

        <fieldset className="flex flex-col gap-1" data-summaries>
          <legend className="text-dense text-ink">{t('report.summarise')}</legend>
          {spec.summaries.map((summary, index) => (
            <span key={`${summary.fn}-${String(index)}`} className="flex items-center gap-1">
              <select
                data-summary-fn={String(index)}
                aria-label={t('report.summarise')}
                value={summary.fn}
                onChange={(event) => {
                  const fn = event.target.value as (typeof FUNCTIONS)[number];
                  const next = [...spec.summaries];
                  // Switching to count drops the field: counting rows has no
                  // field, and leaving a stale one would save a spec whose
                  // field is ignored and whose next edit resurrects it.
                  next[index] = { fn, field: fn === 'count' ? null : (summary.field ?? firstSummarisable(source)) };
                  update({ summaries: next });
                }}
                className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
              >
                {FUNCTIONS.map((fn) => (
                  <option key={fn} value={fn}>
                    {t(`report.fn.${fn}` as 'report.fn.count')}
                  </option>
                ))}
              </select>
              {summary.fn === 'count' ? null : (
                <select
                  data-summary-field={String(index)}
                  aria-label={t('report.value')}
                  value={summary.field ?? ''}
                  onChange={(event) => {
                    const next = [...spec.summaries];
                    next[index] = { ...summary, field: event.target.value };
                    update({ summaries: next });
                  }}
                  className="h-input min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 text-dense text-ink"
                >
                  {source.fields
                    .filter((field) => field.summarisable)
                    .map((field) => (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    ))}
                </select>
              )}
              <Button
                intent="ghost"
                size="small"
                icon="X"
                data-remove-summary={String(index)}
                onClick={() =>
                  update({ summaries: spec.summaries.filter((_, at) => at !== index) })
                }
              >
                {t('report.remove')}
              </Button>
            </span>
          ))}
          <Button
            intent="secondary"
            size="small"
            icon="Plus"
            data-add-summary
            onClick={() => update({ summaries: [...spec.summaries, { fn: 'count', field: null }] })}
          >
            {t('report.add')}
          </Button>
        </fieldset>

        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('report.chart')}
          <select
            data-chart-shape-select
            aria-label={t('report.chart')}
            value={spec.chart}
            onChange={(event) => update({ chart: event.target.value as ReportSpec['chart'] })}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          >
            {CHART_SHAPES.map((shape) => (
              <option key={shape} value={shape}>
                {t(`report.chart.${shape}` as 'report.chart.table')}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1">
          <SectionLabel>{t('report.filters')}</SectionLabel>
          {/* The one RuleBuilder, reading a schema built from this source's
              own fields. Not a fork: the difference is the schema. */}
          <RuleBuilder
            data-report-filters
            schema={schemaFor(source)}
            value={spec.filters as RuleNode}
            onChange={(filters) => update({ filters: filters as ReportSpec['filters'] })}
            matchCount={preview.data?.result?.totalCount ?? null}
            labels={ruleBuilderLabels(t)}
          />
        </div>

        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('report.visibility')}
          <select
            data-report-visibility
            aria-label={t('report.visibility')}
            value={visibility}
            onChange={(event) => setVisibility(event.target.value as 'private' | 'workspace')}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          >
            <option value="private">{t('report.visibility.private')}</option>
            <option value="workspace">{t('report.visibility.workspace')}</option>
          </select>
        </label>

        <Button
          intent="primary"
          icon="FloppyDisk"
          data-save-report
          busy={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isSuccess ? t('report.saved') : t('report.save')}
        </Button>
      </aside>
    </div>
  );
}

function firstSummarisable(source: Source): string | null {
  return source.fields.find((field) => field.summarisable)?.key ?? null;
}

/**
 * The preview's groups as chart segments.
 *
 * The value shown is the first summary's, or the group's count when there is
 * none. Already-formatted text goes to `value`; the plain number goes to
 * `magnitude` separately, because a bar's height is geometry and must never be
 * parsed back out of a formatted figure.
 */
function segmentsOf(preview: Preview | undefined, format: ReturnType<typeof useFormat>): ChartSegment[] {
  const result = preview?.result;
  if (!result) return [];
  return result.groups.map((group) => {
    const first = group.values[0];
    const summary = result.summaries[0];
    const isCount = !summary || summary.fn === 'count';
    const magnitude = isCount ? group.count : Number(first ?? 0);
    return {
      key: group.key.join('|') || 'all',
      label: group.label,
      value: isCount ? formatCount(group.count, format) : (first ?? '—'),
      magnitude: Number.isFinite(magnitude) ? magnitude : 0,
      detail: isCount ? undefined : [formatCount(group.count, format)],
      ...(group.drillThrough ? { href: group.drillThrough } : {}),
    };
  });
}

function ReportBuilderEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('report.empty')}</p>;
}

function ReportBuilderLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-plot w-panel-narrow" />
      {/* A preview skeleton shaped like the table it becomes. */}
      <Skeleton className="h-plot flex-1" />
      <Skeleton className="h-plot w-panel-wide" />
    </div>
  );
}

function ReportBuilderError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The report builder could not be loaded"
      body={error instanceof Error ? error.message : 'The report sources did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-23',
  route: '/reports/:id',
  surface: 'page',
  title: 'Report builder',
  features: ['F-097'],
  entitlement: 'F-097',
  reachedBy: [
    { kind: 'nav', item: 'reports' },
    { kind: 'screen', from: 'SC-24', how: 'a widget heading' },
  ],
  components: ['ChartWidget', 'RuleBuilder', 'DataTable'],
  elements: ['ReportBuilder'],
  sampleParams: { id: 'new' },
  states: {
    empty: ReportBuilderEmpty,
    loading: ReportBuilderLoading,
    error: ReportBuilderError,
  },
  component: ReportBuilderScreen,
});

export default screen;
